import { DrillingMultiAnalysisState } from '@go-widgets/well-logs-widget';
import type { DrillingMultiAnalysisInitParams, DrillingMultiAnalysisStateParams } from '@go-widgets/well-logs-widget';
import { STYLE_VARIANTS, type GlobalConfig } from '@repo/go-ui';
import { action, computed, flow, makeObservable, observable, reaction } from 'mobx';

import { lagacyAgent } from 'src/api/legacyAgent';
import { wellLogsWidgetAgent } from 'src/api/wellLogsWidgetAgent';
import type { TGroup, TWellListCombinedData } from 'src/entities/tab/TabEntity';
import { requireService } from 'src/packages/di';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';
import { getCorrectLocale } from '../well-day-depth-widget/utils';

import type { CalculationStatusType } from './CalculationStatus';
import type { DrillingMultiAnalysisWidgetEntity } from './DrillingMultiAnalysisWidget.entity';

export class DrillingMultiAnalysisWidgetStore {
  private readonly entity: DrillingMultiAnalysisWidgetEntity;
  private readonly groupSelectStore: GroupSelectStore;
  private readonly initParams: DrillingMultiAnalysisInitParams;

  @observable isReady: boolean = false;
  @observable isExitFullscreenFetching: boolean = false;
  @observable progress: number | null = null;
  @observable isGroupSelectOpen: boolean = false;

  constructor(
    entity: DrillingMultiAnalysisWidgetEntity,
    groupSelectStore: GroupSelectStore,
    private readonly wellListPanel: TWellListCombinedData,
    private readonly theme = requireService('theme'),
    private readonly stompCachePublisherService = requireService('stompCachePublisherService'),
    private readonly wellStatusService = requireService('wellStatusService'),
    private readonly language = requireService('language'),
    private readonly classOfUnitsService = requireService('classOfUnitsService'),
    private readonly dashboardService = requireService('dashboardService'),
    private readonly wellboresRegistry = requireService('wellboresRegistry'),
    private readonly userPreferencesService = requireService('userPreference'),
    private readonly notifications = requireService('notifications'),
  ) {
    this.entity = entity;
    this.groupSelectStore = groupSelectStore;
    this.isGroupSelectOpen = entity.isGroupsDefaultOpened;

    this.initParams = {
      axiosInstance: wellLogsWidgetAgent,
      legacyAxiosInstance: lagacyAgent,
      stompPublisher: this.stompCachePublisherService.stompPublisher,
      wellDirectoryServiceStompPublisher: this.wellStatusService,
      classOfUnitsService: this.classOfUnitsService,
      dashboardService: this.dashboardService,
      wellboresRegistry: this.wellboresRegistry,
      defaultDepthUnits:
        this.userPreferencesService.preferences.defaultDepthUnits === 'INDEX_DEPTH_SCALE_METERS' ? 'm' : 'ft',
      splitRange: {
        time: this.userPreferencesService.preferences.defaultSplitRange.time,
        depth: this.userPreferencesService.preferences.defaultSplitRange.depth,
      },
      updateReferenceWells: () => {
        return this.entity.allWells;
      },
    };

    makeObservable(this);
  }

  @action.bound
  setIsGroupSelectOpen(isOpen: boolean): void {
    this.isGroupSelectOpen = isOpen;
  }

  @action.bound
  setGroupForWidget(group: TGroup | null): void {
    this.entity.setGroup(group ? group.id : null);
  }

  @computed
  get isWellListLoading(): boolean {
    return this.wellListPanel.store.isLoading;
  }

  @flow.bound
  async *exitFullScreen(themeConfig: GlobalConfig) {
    if (this.isExitFullscreenFetching) return;

    this.isExitFullscreenFetching = true;

    this.entity.state?.setAppParams({
      fullScreen: false,
      theme: [STYLE_VARIANTS.dark, STYLE_VARIANTS.light].includes(themeConfig.theme) ? themeConfig.theme : undefined,
    });

    yield;

    this.entity.setFullscreen(false);

    this.isExitFullscreenFetching = false;
  }

  @action.bound
  private setReady(value: boolean): void {
    this.isReady = value;
  }

  @action.bound
  private setProgress(value: number | null) {
    this.progress = value;
  }

  @action.bound
  private setInActiveTab(value: boolean) {
    this.entity.setInActiveTab(value);
  }

  private getNewState(wellId: number): DrillingMultiAnalysisState {
    const stateParams = this.entity.stateParams;

    const eventHandlers = {
      onInternalStateChanged: (state: Partial<DrillingMultiAnalysisStateParams>) => {
        this.entity.setStateParams({ ...this.entity.stateParams, ...state });
      },
      onInitializedSuccess: () => {
        this.setReady(true);
      },
      onInitializationFailed: (_: string, error?: unknown) => {
        this.setReady(false);
        console.error(error);
        this.notifications.showErrorMessageT('errors:failedToInitWidget');
      },
      onWellboreChanged: (wellboreName: string) => {
        this.entity.setWellboreName(wellboreName);
      },
      onLoaderStatusChanged: (value: number | null) => {
        this.setProgress(value ? Math.round(value) : value);
      },
      onCorridorsCalculationStatusChanged: (status: CalculationStatusType) => {
        this.entity.setCorridorsCalculationStatus(status);
      },
    };

    const appParams = {
      wellId,
      width: 500,
      height: 500,
      theme: this.theme.theme,
      locale: getCorrectLocale(this.language.language),
      isAltNames: this.userPreferencesService.preferences.isAltNames,
      isColoredHeaders: this.userPreferencesService.preferences.isColoredParamsHeaders,
      curveRenderingMode: 'mesh',
      fullScreen: this.entity.isFullscreen,
      active: this.entity.inActiveTab,
      suppressTooltips: false,
      defaultSelectWellbore: this.userPreferencesService.preferences.autoSelectWellboreForDepth,
    } as const;

    return new DrillingMultiAnalysisState(this.initParams, appParams, stateParams, eventHandlers);
  }

  effect = () => {
    this.setInActiveTab(true);

    const handleVisibilityChange = (): void => {
      this.setInActiveTab(document.visibilityState === 'visible');
    };
    window.addEventListener('visibilitychange', handleVisibilityChange);

    const disposeGroupId = reaction(
      () => this.entity.groupId,
      (groupId) => {
        if (groupId === null) return;
        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.entity, this.wellListPanel);
      },
    );

    const disposeWellId = reaction(
      () => ({
        wellId: this.entity.wellId,
        wellsLoading: this.isWellListLoading,
      }),
      ({ wellId, wellsLoading }, prev) => {
        if (wellsLoading) {
          return;
        }

        const prevWellId = prev?.wellId;

        if (wellId && !this.entity.state) {
          const newState = this.getNewState(wellId);
          this.entity.setState(newState);
          this.setReady(true);
        } else if (wellId && this.entity.state && typeof prevWellId !== 'undefined') {
          this.entity.state.setWell(wellId);
        } else if (!wellId && this.entity.state) {
          this.entity.state.destroy();
          this.entity.setState(null);
        }
        this.setProgress(null);
      },
      { fireImmediately: true },
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
        isColoredHeaders: this.userPreferencesService.preferences.isColoredParamsHeaders,
      }),
      ({ theme, language, isAltNames, isColoredHeaders }) => {
        this.entity.state?.setAppParams({
          theme,
          locale: getCorrectLocale(language),
          isAltNames,
          isColoredHeaders,
        });
      },
      { fireImmediately: true },
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.store.selectedWellId,
      (wellId) => {
        if (this.wellListPanel.panelState?.groupId === this.entity.groupId) {
          this.entity.setWellId(wellId);
        }
      },
      { fireImmediately: true },
    );

    return () => {
      this.setInActiveTab(false);
      disposeGroupId();
      disposeWellId();
      disposeStateParams();
      wellListPanelDisposer();
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };
}
